import { useEffect, useMemo, useState } from 'react'
import {
  ChartBar, ArrowsClockwise, FileCsv, FilePdf, CheckCircle, Warning, MinusCircle,
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from './exportHelpers'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A', slate: '#7A8FA0',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}

const CARD = { background: C.bg, borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }

const STATUS = {
  ok:      { color: C.olive, label: 'Reportable',  Icon: CheckCircle },
  partial: { color: C.amber, label: 'Parcial',     Icon: Warning },
  pending: { color: '#B0A89E', label: 'Pendiente', Icon: MinusCircle },
}

// Factores
const GRID_FACTOR_ES = 0.181  // kg CO2e/kWh España 2024 (REE)
const GAS_FACTOR = 0.20       // kg CO2e/kWh combustión gas (MITECO)

function parseJ(ev) {
  if (!ev?.justificacion) return null
  try { return JSON.parse(ev.justificacion) } catch { return null }
}

function catPct(evaluations, BREEAM_CRITERIA, ...catNames) {
  let obtained = 0, max = 0
  catNames.forEach(cat => {
    const data = BREEAM_CRITERIA?.[cat]
    if (!data) return
    Object.entries(data.criteria).forEach(([code, c]) => {
      const ev = evaluations?.[`${cat}-${code}`]
      let isNA = false
      try { isNA = !!JSON.parse(ev?.justificacion || 'null')?.no_aplica } catch {}
      if (isNA) return
      max += (ev?.points_max ?? c.points ?? 0)
      obtained += (ev?.puntos_auditoria ?? ev?.points_obtained) || 0
    })
  })
  return max === 0 ? null : { pct: Math.round((obtained / max) * 100), obtained, max }
}

function aggregateSensors(rows) {
  if (!rows?.length) return null
  const yMap = new Map()
  rows.forEach(r => {
    const a = yMap.get(r.reading_year) || {
      year: r.reading_year, months: 0,
      kwh: 0, kwh_renew: 0, electricity_kwh: 0, gas_kwh: 0, district_kwh: 0,
      m3: 0, m3_potable: 0, m3_reclaimed: 0,
      waste_t: 0, waste_recycled_t: 0, waste_landfill_t: 0, waste_hazardous_t: 0,
      s1: 0, s2: 0, s3: 0,
      iaq_co2: 0, iaq_pm25: 0, iaq_n: 0,
    }
    a.months += 1
    a.kwh += +(r.energy_kwh_total || 0)
    a.kwh_renew += +(r.energy_kwh_renewable || 0)
    a.electricity_kwh += +(r.electricity_kwh || 0)
    a.gas_kwh += +(r.gas_kwh || 0)
    a.district_kwh += +(r.district_heat_kwh || 0)
    a.m3 += +(r.water_m3_total || 0)
    a.m3_potable += +(r.water_m3_potable || 0)
    a.m3_reclaimed += +(r.water_m3_reclaimed || 0)
    a.waste_t += +(r.waste_tons_total || 0)
    a.waste_recycled_t += +(r.waste_tons_recycled || 0)
    a.waste_landfill_t += +(r.waste_tons_landfill || 0)
    a.waste_hazardous_t += +(r.waste_tons_hazardous || 0)
    a.s1 += +(r.scope1_kgco2e || 0)
    a.s2 += +(r.scope2_kgco2e || 0)
    a.s3 += +(r.scope3_kgco2e || 0)
    if (r.iaq_co2_ppm_avg) { a.iaq_co2 += +r.iaq_co2_ppm_avg; a.iaq_n += 1 }
    if (r.iaq_pm25_avg) a.iaq_pm25 += +r.iaq_pm25_avg
    yMap.set(r.reading_year, a)
  })
  const years = [...yMap.keys()].sort((a, b) => b - a)
  const y = yMap.get(years[0])
  if (y.iaq_n > 0) y.iaq_co2 = y.iaq_co2 / y.iaq_n
  // Si energy_kwh_total no se rellenó directamente, calcular como suma de componentes
  if (y.kwh === 0) {
    y.kwh = y.electricity_kwh + y.gas_kwh + y.district_kwh + y.kwh_renew
    y.kwh_derived = true
  }
  return y
}

// ── Definición de los 13 estándares GRI derivables de BREEAM ──────────────────
const GRI_STANDARDS = [
  {
    id: '201', name: 'Desempeño económico', series: '200', accent: C.slate,
    disclosures: [
      { code: '201-2', name: 'Implicaciones financieras y otros riesgos y oportunidades del cambio climático', src: 'iros D7/D8/D10/D11/D12/D13' },
    ],
  },
  {
    id: '203', name: 'Impactos económicos indirectos', series: '200',accent: C.slate,
    disclosures: [
      { code: '203-1', name: 'Inversión en infraestructuras y servicios apoyados', src: 'BREEAM TRA 1 + capex' },
      { code: '203-2', name: 'Impactos económicos indirectos significativos',        src: 'TRA 1 IA + GST 4' },
    ],
  },
  {
    id: '204', name: 'Prácticas de adquisición', series: '200',accent: C.slate,
    disclosures: [
      { code: '204-1', name: '% gasto en proveedores con sourcing certificado', src: 'BREEAM MAT 3' },
    ],
  },
  {
    id: '301', name: 'Materiales', series: '300',accent: C.olive,
    disclosures: [
      { code: '301-1', name: 'Materiales utilizados por peso o volumen',           src: 'Econ Circular (inventario)' },
      { code: '301-2', name: 'Insumos reciclados utilizados (%)',                  src: 'Econ Circular MOI / RSD 2' },
      { code: '301-3', name: 'Productos reutilizados / materiales recuperados',    src: 'Econ Circular DI/RPI' },
    ],
  },
  {
    id: '302', name: 'Energía', series: '300',accent: C.olive,
    disclosures: [
      { code: '302-1',   name: 'Consumo energético total dentro de la organización', src: 'building_sensors' },
      { code: '302-1.b', name: 'Energía renovable in situ (FV / generación propia)', src: 'building_sensors' },
      { code: '302-3',   name: 'Intensidad energética',                              src: 'BREEAM ENE 1 / sensors' },
      { code: '302-4',   name: 'Reducción del consumo energético',                   src: 'ENE 1 vs base CTE' },
      { code: '302-5',   name: 'Reducción en requisitos energéticos de productos',   src: 'ENE 6 transporte interno' },
    ],
  },
  {
    id: '303', name: 'Agua y efluentes', series: '300',accent: C.olive,
    disclosures: [
      { code: '303-1', name: 'Interacciones con el agua',                          src: 'BREEAM AG 1-4' },
      { code: '303-3', name: 'Captación de agua',                                  src: 'sensors' },
      { code: '303-4', name: 'Vertido de agua / reutilización',                    src: 'sensors' },
      { code: '303-5', name: 'Consumo de agua',                                    src: 'sensors / AG 1' },
    ],
  },
  {
    id: '304', name: 'Biodiversidad', series: '300',accent: C.olive,
    disclosures: [
      { code: '304-1', name: 'Centros operacionales próximos a áreas protegidas', src: 'BREEAM USE 1' },
      { code: '304-2', name: 'Impactos significativos sobre la biodiversidad',    src: 'USE 2' },
      { code: '304-3', name: 'Hábitats protegidos o restaurados',                 src: 'USE 4' },
      { code: '304-4', name: 'Especies en lista roja UICN afectadas',             src: 'USE 5' },
    ],
  },
  {
    id: '305', name: 'Emisiones', series: '300',accent: C.olive,
    disclosures: [
      { code: '305-1', name: 'Emisiones directas (Scope 1)',                       src: 'sensors gas / ENE 1' },
      { code: '305-2', name: 'Emisiones indirectas energía (Scope 2)',             src: 'sensors electricidad / ENE 1' },
      { code: '305-3', name: 'Otras emisiones indirectas (Scope 3)',               src: 'MAT 1 LCA + RSD 1' },
      { code: '305-4', name: 'Intensidad de emisiones',                            src: 'ENE 1 CO₂/m²' },
      { code: '305-7', name: 'NOx, SOx y otras emisiones significativas',          src: 'BREEAM CONT 2' },
    ],
  },
  {
    id: '306', name: 'Residuos', series: '300',accent: C.olive,
    disclosures: [
      { code: '306-1', name: 'Generación de residuos e impactos significativos',  src: 'BREEAM RSD 1 / sensors' },
      { code: '306-3', name: 'Residuos generados',                                 src: 'RSD 1 / sensors' },
      { code: '306-4', name: 'Residuos no destinados a eliminación (desvío)',     src: 'RSD 1 tasa desvío' },
      { code: '306-5', name: 'Residuos destinados a eliminación',                  src: 'RSD 1 / sensors' },
    ],
  },
  {
    id: '308', name: 'Evaluación ambiental de proveedores', series: '300',accent: C.olive,
    disclosures: [
      { code: '308-1', name: '% nuevos proveedores con criterios ambientales',     src: 'BREEAM MAT 3 (BES 6001)' },
      { code: '308-2', name: 'Impactos ambientales negativos en la cadena',        src: 'MAT 3 categoría sourcing' },
    ],
  },
  {
    id: '403', name: 'Salud y seguridad ocupacional', series: '400',accent: C.coral,
    disclosures: [
      { code: '403-3', name: 'Servicios de salud (calidad agua / aire)',           src: 'SyB 2, SyB 4' },
      { code: '403-6', name: 'Promoción de la salud',                              src: 'SyB 1, 2, 3, 5' },
      { code: '403-7', name: 'Prevención impactos de actividad (NOx, ruido)',      src: 'CONT 2, CONT 5' },
    ],
  },
  {
    id: '404', name: 'Formación y enseñanza', series: '400',accent: C.coral,
    disclosures: [
      { code: '404-2', name: 'Programas para mejorar aptitudes de empleados',      src: 'GST 1 (formación operadores)' },
    ],
  },
  {
    id: '413', name: 'Comunidades locales', series: '400',accent: C.coral,
    disclosures: [
      { code: '413-1', name: '% operaciones con engagement comunitario',           src: 'BREEAM GST 4' },
      { code: '413-2', name: 'Operaciones con impactos negativos significativos',  src: 'TRA + CONT' },
    ],
  },
  {
    id: '414', name: 'Evaluación social de proveedores', series: '400',accent: C.coral,
    disclosures: [
      { code: '414-1', name: '% proveedores evaluados socialmente',                src: 'MAT 3 (BES 6001 incluye social)' },
    ],
  },
  {
    id: '416', name: 'Salud y seguridad del cliente (ocupante)', series: '400',accent: C.coral,
    disclosures: [
      { code: '416-1', name: 'Evaluación de impactos en salud y seguridad',        src: 'SyB 4, SyB 7' },
    ],
  },
]

// ── Componente principal ─────────────────────────────────────────────────────
export default function InformeGRI({ project, BREEAM_CRITERIA }) {
  const [sensors, setSensors] = useState(null)
  const [ci, setCi] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [seriesFilter, setSeriesFilter] = useState('all')
  const [iros, setIros] = useState([])

  const loadLive = async () => {
    if (!project?.id) return
    setLoading(true); setError(null)
    const [s, c, iroRes] = await Promise.all([
      supabase.from('building_sensors').select('*').eq('project_id', project.id).order('reading_year', { ascending: false }),
      supabase.from('esg_snapshots').select('value_numeric').eq('project_id', project.id).eq('framework', 'circular').eq('indicator_code', 'DGNB_CI').order('created_at', { ascending: false }).limit(1),
      supabase.from('operational_iros').select('*').eq('project_id', project.id).neq('status', 'dismissed').neq('status', 'closed'),
    ])
    setLoading(false)
    if (s.error || c.error) { setError((s.error || c.error).message); return }
    setSensors(aggregateSensors(s.data))
    setCi(c.data?.[0]?.value_numeric ?? null)
    setIros(iroRes.data || [])
  }
  // loadLive depende solo de project?.id (ya en deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLive() }, [project?.id])

  // Estabilizamos la referencia con useMemo para que los useMemo que
  // dependen de `evaluations` no se disparen en cada render cuando
  // project?.evaluations es undefined.
  const evaluations = useMemo(() => project?.evaluations || {}, [project?.evaluations])

  // ── Atajos de extracción de BREEAM ──
  const ene1J = parseJ(evaluations['Energía-ENE 1'])
  const mat1J = parseJ(evaluations['Materiales-MAT 1'])
  const mat3J = parseJ(evaluations['Materiales-MAT 3'])
  const rsd1J = parseJ(evaluations['Residuos-RSD 1'])
  const rsd2J = parseJ(evaluations['Residuos-RSD 2'])
  const tra1J = parseJ(evaluations['Transporte-TRA 1'])
  const gst1J = parseJ(evaluations['Gestión-GST 1'])
  const gst4J = parseJ(evaluations['Gestión-GST 4'])
  const cont2 = catPct(evaluations, BREEAM_CRITERIA, 'Contaminación')
  const matCat = catPct(evaluations, BREEAM_CRITERIA, 'Materiales')
  const sybCat = catPct(evaluations, BREEAM_CRITERIA, 'Salud y Bienestar')
  const aguaCat = catPct(evaluations, BREEAM_CRITERIA, 'Agua')

  // m² de cálculo
  const m2 = useMemo(() => {
    const z = ene1J?.ene1_calculadora?.zonas
    if (Array.isArray(z)) {
      const t = z.filter(x => parseFloat(x.m2) > 0).reduce((s, x) => s + parseFloat(x.m2), 0)
      if (t > 0) return t
    }
    return project?.surface_m2 || null
  }, [ene1J, project?.surface_m2])

  // ENE 1 derived
  const ene1Co2 = useMemo(() => {
    const z = ene1J?.ene1_calculadora?.zonas?.filter(x => parseFloat(x.m2) > 0)
    if (!z?.length) return null
    const t = z.reduce((s, x) => s + parseFloat(x.m2), 0)
    return z.reduce((s, x) => s + parseFloat(x.co2_eval || 0) * parseFloat(x.m2), 0) / t
  }, [ene1J])
  const ene1Energy = useMemo(() => {
    const z = ene1J?.ene1_calculadora?.zonas?.filter(x => parseFloat(x.m2) > 0)
    if (!z?.length) return null
    const t = z.reduce((s, x) => s + parseFloat(x.m2), 0)
    return z.reduce((s, x) => s + parseFloat(x.con_eval || 0) * parseFloat(x.m2), 0) / t
  }, [ene1J])

  // ── Cálculo de valores por disclosure ──
  const disclosureValues = useMemo(() => {
    const V = (value, status, detail) => ({ value, status, detail })

    // Ayudas IRO
    const findIro = (id) => iros.find(i => i.detector_id === id)
    const climaticRisks = iros.filter(i => (i.type === 'risk' || i.type === 'impact') && (i.category || '').includes('climate'))
    const opportunities = iros.filter(i => i.type === 'opportunity')
    const totalRiskEur  = climaticRisks.reduce((s, i) => s + (Number(i.financial_estimate_eur) || 0), 0)
    const totalOppEur   = opportunities.reduce((s, i) => s + (Number(i.financial_estimate_eur) || 0), 0)
    const d14 = findIro('D14.performance_gap')

    return {
      // 201 — Desempeño económico
      '201-2': climaticRisks.length > 0 || opportunities.length > 0
        ? V(
            `${climaticRisks.length} riesgo${climaticRisks.length === 1 ? '' : 's'} + ${opportunities.length} oportunidad${opportunities.length === 1 ? '' : 'es'}${totalRiskEur + totalOppEur > 0 ? ' · ' + Math.round(totalRiskEur + totalOppEur).toLocaleString('es-ES') + ' €/año' : ''}`,
            'ok',
            `Riesgos climáticos y oportunidades detectados en el módulo IRO operativo: ${[...climaticRisks, ...opportunities].map(i => (i.detector_id || '').split('.')[0]).join(', ')}. Trazabilidad completa disponible en la pestaña Monitorización → IROs.`
          )
        : V('—', 'pending', 'Ejecuta el módulo Monitorización → IROs (D7, D8, D10, D11 riesgos · D12, D13 oportunidades) para poblar automáticamente este disclosure.'),
      // 203
      '203-1': tra1J?.tra1_calculadora?.ia != null
        ? V(`IA = ${tra1J.tra1_calculadora.ia}`, tra1J.tra1_calculadora.ia >= 4 ? 'ok' : 'partial', 'BREEAM TRA 1 — Índice de accesibilidad al transporte público (impacto económico indirecto del activo en su entorno: facilita acceso de empleados y clientes).')
        : V('—', 'pending', 'Carga TRA 1.'),
      '203-2': gst4J ? V('GST 4 evaluado', 'partial', 'Evaluación de impactos en el entorno realizada (GST 4 Stakeholders).') : V('—', 'pending', 'Evalúa GST 4.'),
      // 204
      '204-1': (() => {
        const ev = evaluations['Materiales-MAT 3']
        if (!ev) return V('—', 'pending', 'Evalúa MAT 3 BREEAM.')
        const pts = (ev.puntos_auditoria ?? ev.points_obtained) || 0
        const maxPts = ev.points_max || 3
        const tramos = [
          { range: '< 18% responsable',  status: 'pending' },
          { range: '18-36% responsable', status: 'partial' },
          { range: '36-54% responsable', status: 'ok' },
          { range: '≥ 54% responsable',  status: 'ok' },
        ]
        const t = tramos[Math.min(pts, 3)]
        return V(`MAT 3: ${pts}/${maxPts} pts · ${t.range}`, t.status,
          'BREEAM MAT 3 — % de elementos con sourcing certificado (BES 6001, EN 17080, FSC, PEFC, ISO 14001) según Tabla 35.')
      })(),
      // 301
      '301-1': ci != null
        ? V(`CI = ${ci.toFixed(2)} (DGNB)`, 'ok', 'Inventario en módulo Economía Circular (Building Resource Passport).')
        : V('—', 'pending', 'Llena inventario en Economía Circular.'),
      '301-2': rsd2J?.rsd2_calculadora?.porcentaje_reciclado != null
        ? V(`${rsd2J.rsd2_calculadora.porcentaje_reciclado}% áridos reciclados`, rsd2J.rsd2_calculadora.porcentaje_reciclado >= 25 ? 'ok' : 'partial', 'BREEAM RSD 2 — áridos reciclados in situ.')
        : V('—', 'pending', 'Carga RSD 2.'),
      '301-3': ci != null
        ? V(`CI post-uso disponible`, ci >= 0.20 ? 'ok' : 'partial', 'Sub-indicadores Econ Circular DI (desmontabilidad) + RPI (reciclaje).')
        : V('—', 'pending', 'Llena Econ Circular.'),
      // 302
      '302-1': sensors?.kwh > 0
        ? V(`${Math.round(sensors.kwh).toLocaleString()} kWh/año`, 'ok', `Sensores año ${sensors.year}${sensors.kwh_derived ? ' (calculado como suma de componentes)' : ''}: electricidad ${Math.round(sensors.electricity_kwh).toLocaleString()} + gas ${Math.round(sensors.gas_kwh).toLocaleString()} + renovable ${Math.round(sensors.kwh_renew).toLocaleString()}.`)
        : V('—', 'pending', 'Carga building_sensors.'),
      '302-1.b': sensors?.kwh_renew > 0
        ? V(`${Math.round(sensors.kwh_renew).toLocaleString()} kWh/año${sensors.kwh > 0 ? ` · ${Math.round(sensors.kwh_renew / sensors.kwh * 100)}% del total` : ''}`, 'ok', `Generación propia in situ (FV o similar). Año ${sensors.year}.`)
        : V('—', 'pending', 'Carga energy_kwh_renewable.'),
      '302-3': ene1Energy != null
        ? V(`${ene1Energy.toFixed(1)} kWh/m²·año`, 'ok', 'BREEAM ENE 1 calculadora (energía primaria).')
        : sensors?.kwh && m2 ? V(`${(sensors.kwh / m2).toFixed(1)} kWh/m²·año`, 'ok', `Sensores año ${sensors.year} / ${m2} m².`)
        : V('—', 'pending', 'Carga ENE 1 o sensors.'),
      '302-4': ene1J?.ene1_calculadora?.eprnc != null
        ? V(`EPRNC = ${ene1J.ene1_calculadora.eprnc.toFixed(2)} (vs ref CTE-HE)`, ene1J.ene1_calculadora.eprnc >= 0.60 ? 'ok' : 'partial', 'Reducción vs edificio de referencia CTE HE (banda C según mem).')
        : V('—', 'pending', 'Carga ENE 1.'),
      '302-5': (() => {
        const ene6 = evaluations['Energía-ENE 6']
        if (!ene6) return V('—', 'pending', 'Evalúa ENE 6.')
        const pts = (ene6.puntos_auditoria ?? ene6.points_obtained) || 0
        const max = ene6.points_max || 2
        return V(`ENE 6: ${pts}/${max} pts`, pts >= max ? 'ok' : 'partial',
          'BREEAM ENE 6 — sistemas de transporte energéticamente eficientes (ascensores con motor eficiente y recuperación de energía).')
      })(),
      // 303
      '303-1': (() => {
        const ag1c = parseJ(evaluations['Agua-AG 1'])?.ag1_calculadora
        if (ag1c?.nivelGlobal != null) {
          return V(`AG 1 nivel global ${ag1c.nivelGlobal.toFixed(2)} (${ag1c.buildingType || 'Edificio'})`,
            ag1c.nivelGlobal >= 3 ? 'ok' : 'partial',
            `BREEAM AG 1 calculadora — eficiencia hídrica de inodoros/grifos/duchas. Sistema de aguas: ${ag1c.sistemaAguas || 'estándar'}.`)
        }
        return aguaCat?.pct != null
          ? V(`${aguaCat.obtained}/${aguaCat.max} pts AG`, aguaCat.pct >= 55 ? 'ok' : 'partial', 'Interacciones con el agua evaluadas en criterios AG 1-4.')
          : V('—', 'pending', 'Evalúa Agua.')
      })(),
      '303-3': sensors?.m3 != null
        ? V(`${Math.round(sensors.m3).toLocaleString()} m³/año`, 'ok', `Sensores año ${sensors.year}.`)
        : V('—', 'pending', 'Carga building_sensors.'),
      '303-4': sensors?.m3_reclaimed != null && sensors.m3 > 0
        ? V(`${Math.round(sensors.m3_reclaimed).toLocaleString()} m³ reutilizados (${Math.round(sensors.m3_reclaimed / sensors.m3 * 100)}%)`, sensors.m3_reclaimed > 0 ? 'ok' : 'partial', 'Sensores reutilización.')
        : V('—', 'pending', 'Carga sensors con m³ reclaimed.'),
      '303-5': sensors?.m3 && m2 ? V(`${(sensors.m3 / m2).toFixed(2)} m³/m²·año`, 'ok', 'Intensidad hídrica operacional.')
        : V('—', 'pending', 'Carga sensors + superficie ENE 1.'),
      // 304
      '304-1': evaluations['Uso del Suelo y Ecología-USE 1']
        ? V('USE 1 evaluado', 'partial', 'Análisis de selección de emplazamiento (USE 1).')
        : V('—', 'pending', 'Evalúa USE 1.'),
      '304-2': ((evaluations['Uso del Suelo y Ecología-USE 2']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 2']?.points_obtained)) > 0
        ? V(`USE 2: ${(evaluations['Uso del Suelo y Ecología-USE 2'].puntos_auditoria ?? evaluations['Uso del Suelo y Ecología-USE 2'].points_obtained)}/2 pts`, 'ok', 'Protección elementos valor ecológico.')
        : V('—', 'pending', 'Evalúa USE 2.'),
      '304-3': ((evaluations['Uso del Suelo y Ecología-USE 4']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 4']?.points_obtained)) > 0
        ? V(`USE 4: ${(evaluations['Uso del Suelo y Ecología-USE 4'].puntos_auditoria ?? evaluations['Uso del Suelo y Ecología-USE 4'].points_obtained)}/3 pts (mejora ecológica)`, 'ok', 'Hábitats restaurados via plan ecológico.')
        : V('—', 'pending', 'Evalúa USE 4.'),
      '304-4': ((evaluations['Uso del Suelo y Ecología-USE 5']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 5']?.points_obtained)) > 0
        ? V(`USE 5: impacto a largo plazo evaluado`, 'partial', 'Cualitativo — registrar especies UICN si aplica.')
        : V('—', 'pending', 'Evalúa USE 5.'),
      // 305
      '305-1': sensors?.s1 > 0
        ? V(`${(sensors.s1 / 1000).toFixed(2)} tCO₂e`, 'ok', `Sensores año ${sensors.year} (gas natural + combustible in situ).${d14 ? ' D14 detectado: gap real vs CEE +' + (d14.detector_output?.excess_pct || '?') + '%.' : ''}`)
        : sensors?.gas_kwh > 0
          ? V(`${(sensors.gas_kwh * GAS_FACTOR / 1000).toFixed(2)} tCO₂e (estimado)`, 'partial', `Estimado: gas × ${GAS_FACTOR} kgCO₂/kWh MITECO.`)
        : V('—', 'pending', 'Carga sensors con scope1 o gas.'),
      '305-2': sensors?.s2 > 0
        ? V(`${(sensors.s2 / 1000).toFixed(2)} tCO₂e`, 'ok', `Sensores año ${sensors.year} (electricidad comprada).${d14 ? ' D14 detectado: gap real vs CEE +' + (d14.detector_output?.excess_pct || '?') + '%.' : ''}`)
        : sensors?.electricity_kwh > 0
          ? V(`${(sensors.electricity_kwh * GRID_FACTOR_ES / 1000).toFixed(2)} tCO₂e`, 'ok', `Calculado: electricidad × ${GRID_FACTOR_ES} kgCO₂/kWh REE.${d14 ? ' D14 detectado: gap real vs CEE +' + (d14.detector_output?.excess_pct || '?') + '%.' : ''}`)
        : ene1Co2 != null && m2 ? V(`${(ene1Co2 * m2 / 1000).toFixed(2)} tCO₂e`, 'ok', `Calculado desde ENE 1: ${ene1Co2.toFixed(1)} kgCO₂/m²·año × ${m2} m² (intensidad operacional × superficie).`)
        : V('—', 'pending', 'Carga sensors o ENE 1.'),
      '305-3': mat1J?.mat1_oneclicklca?.co2_m2_year && m2
        ? V(`${(mat1J.mat1_oneclicklca.co2_m2_year * m2 / 1000).toFixed(2)} tCO₂e/año (cat 1)`, 'ok', `MAT 1 OneClick LCA — emisiones embebidas materiales (Cat 1 GHG Protocol).`)
        : sensors?.s3 > 0
          ? V(`${(sensors.s3 / 1000).toFixed(2)} tCO₂e`, 'ok', `Sensores año ${sensors.year}.`)
        : V('—', 'pending', 'Carga MAT 1 LCA o sensors scope3.'),
      '305-4': ene1Co2 != null
        ? V(`${ene1Co2.toFixed(1)} kgCO₂/m²·año`, ene1Co2 < 40 ? 'ok' : 'partial', 'Intensidad operacional ENE 1.')
        : V('—', 'pending', 'Carga ENE 1.'),
      '305-7': (() => {
        const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained)) || 0
        const c2max = evaluations['Contaminación-CNT 2']?.points_max || 3
        if (c2 >= 3) return V(`CNT 2: ${c2}/${c2max} pts (NOx ≤ 70 mg/kWh)`, 'ok', 'BREEAM CNT 2 — caldera/equipos de combustión con NOx por debajo del umbral más estricto.')
        if (c2 >= 2) return V(`CNT 2: ${c2}/${c2max} pts`, 'ok', 'BREEAM CNT 2 — NOx ≤ 90 mg/kWh.')
        if (c2 >= 1) return V(`CNT 2: ${c2}/${c2max} pts`, 'partial', 'BREEAM CNT 2 — NOx ≤ 120 mg/kWh.')
        return cont2?.pct != null
          ? V(`${cont2.obtained}/${cont2.max} pts CONT`, cont2.pct >= 55 ? 'ok' : 'partial', 'Categoría Contaminación evaluada.')
          : V('—', 'pending', 'Evalúa CNT 2.')
      })(),
      // 306
      '306-1': rsd1J ? V('Plan RSD 1 evaluado', 'ok', 'Plan de gestión de residuos de construcción RSD 1.') : V('—', 'pending', 'Carga RSD 1.'),
      '306-3': sensors?.waste_t > 0
        ? V(`${sensors.waste_t.toFixed(1)} t (operación)`, 'ok', `Sensores año ${sensors.year}.`)
        : rsd1J?.rsd1_calculadora?.tons_total
          ? V(`${rsd1J.rsd1_calculadora.tons_total} t (construcción)`, 'partial', 'RSD 1 obra. Para reporte operacional usar sensors.')
        : V('—', 'pending', 'Carga RSD 1 o sensors.'),
      '306-4': sensors?.waste_recycled_t > 0 && sensors.waste_t > 0
        ? V(`${sensors.waste_recycled_t.toFixed(1)} t (${Math.round(sensors.waste_recycled_t / sensors.waste_t * 100)}% desvío)`, 'ok', 'Sensores: residuos reciclados.')
        : rsd1J?.rsd1_calculadora?.tasa_desvio
          ? V(`${rsd1J.rsd1_calculadora.tasa_desvio}% desvío`, rsd1J.rsd1_calculadora.tasa_desvio >= 70 ? 'ok' : 'partial', 'RSD 1 tasa desvío vertedero (construcción).')
        : V('—', 'pending', 'Carga RSD 1 / sensors.'),
      '306-5': sensors?.waste_landfill_t > 0
        ? V(`${sensors.waste_landfill_t.toFixed(1)} t a vertedero`, sensors.waste_landfill_t < 1 ? 'ok' : 'partial', `Sensores año ${sensors.year}.`)
        : V('—', 'pending', 'Carga sensors waste_landfill_t.'),
      // 308
      '308-1': mat3J ? V(`MAT 3 documentado`, 'ok', 'Productos con certificación ambiental (BES 6001, EN 17080, ISO 14001).')
        : V('—', 'pending', 'Carga MAT 3.'),
      '308-2': (() => {
        const mat3 = evaluations['Materiales-MAT 3']
        if (mat3) {
          const pct = mat3J?.mat3_calculadora?.pct
          if (pct != null) return V(`MAT 3: ${pct.toFixed(1)}% sourcing certificado`, pct >= 36 ? 'ok' : 'partial',
            'BREEAM MAT 3 — % de elementos con certificación ambiental del proveedor (BES 6001, EN 17080, ISO 14001, FSC, PEFC).')
          return V(`MAT 3: ${(mat3.puntos_auditoria ?? mat3.points_obtained) || 0}/${mat3.points_max || 3} pts`, ((mat3.puntos_auditoria ?? mat3.points_obtained) || 0) >= 2 ? 'ok' : 'partial',
            'BREEAM MAT 3 — aprovisionamiento responsable de materiales.')
        }
        return matCat?.pct != null
          ? V(`${matCat.obtained}/${matCat.max} pts MAT`, matCat.pct >= 55 ? 'ok' : 'partial', 'Categoría Materiales evaluada.')
          : V('—', 'pending', 'Evalúa MAT 3.')
      })(),
      // 403
      '403-3': ((evaluations['Salud y Bienestar-SyB 4']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 4']?.points_obtained)) > 0
        ? V('SyB 4 cumple (agua potable)', 'ok', 'Calidad del agua potable verificada.')
        : V('—', 'pending', 'Evalúa SyB 4.'),
      '403-6': (() => {
        const s1 = ((evaluations['Salud y Bienestar-SyB 1']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 1']?.points_obtained)) || 0
        const s2 = ((evaluations['Salud y Bienestar-SyB 2']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 2']?.points_obtained)) || 0
        const s3 = ((evaluations['Salud y Bienestar-SyB 3']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 3']?.points_obtained)) || 0
        const s5 = ((evaluations['Salud y Bienestar-SyB 5']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 5']?.points_obtained)) || 0
        const sum = s1 + s2 + s3 + s5
        if (sum === 0 && sybCat?.pct == null) return V('—', 'pending', 'Evalúa Salud y Bienestar.')
        const tags = []
        if (s1 > 0) tags.push(`SyB 1 visual (${s1})`)
        if (s2 > 0) tags.push(`SyB 2 IAQ (${s2})`)
        if (s3 > 0) tags.push(`SyB 3 térmico (${s3})`)
        if (s5 > 0) tags.push(`SyB 5 acústico (${s5})`)
        return V(`${sum}/10 pts SyB 1+2+3+5`, sum >= 4 ? 'ok' : 'partial', tags.join(' · ') || 'SyB confort ocupantes.')
      })(),
      '403-7': (() => {
        const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained)) || 0
        const c5 = ((evaluations['Contaminación-CNT 5']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 5']?.points_obtained)) || 0
        if (c2 + c5 === 0 && cont2?.pct == null) return V('—', 'pending', 'Evalúa CNT 2 y CNT 5.')
        const parts = []
        if (c2 > 0) parts.push(`CNT 2: ${c2}/3 NOx`)
        if (c5 > 0) parts.push(`CNT 5: ${c5}/1 ruido`)
        const sum = c2 + c5
        return V(`${sum}/4 pts CNT 2+5`, c2 >= 2 ? 'ok' : 'partial', (parts.join(' + ')) || 'Prevención impactos NOx + ruido.')
      })(),
      // 404
      '404-2': gst1J?.gst1_subcriterios?.parte_seguimiento === true
        ? V('Plan seguimiento operadores activo', 'ok', 'GST 1 Parte 3 — formación operadores edificio.')
        : evaluations['Gestión-GST 1'] ? V('GST 1 parcial', 'partial', 'Verifica que el plan de seguimiento incluya formación operadores.')
        : V('—', 'pending', 'Evalúa GST 1.'),
      // 413
      '413-1': ((evaluations['Gestión-GST 4']?.puntos_auditoria) ?? (evaluations['Gestión-GST 4']?.points_obtained)) > 0
        ? V(`GST 4: ${(evaluations['Gestión-GST 4'].puntos_auditoria ?? evaluations['Gestión-GST 4'].points_obtained)}/4 pts`, 'ok', 'Participación de partes interesadas (engagement con comunidad).')
        : V('—', 'pending', 'Evalúa GST 4.'),
      '413-2': (() => {
        const c1 = ((evaluations['Contaminación-CNT 1']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 1']?.points_obtained)) || 0
        const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained)) || 0
        const c3 = ((evaluations['Contaminación-CNT 3']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 3']?.points_obtained)) || 0
        const c4 = ((evaluations['Contaminación-CNT 4']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 4']?.points_obtained)) || 0
        const c5 = ((evaluations['Contaminación-CNT 5']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 5']?.points_obtained)) || 0
        const sum = c1 + c2 + c3 + c4 + c5
        if (sum === 0 && cont2?.pct == null) return V('—', 'pending', 'Evalúa Contaminación.')
        const ok = []
        if (c1 > 0) ok.push('refrigerantes')
        if (c2 >= 2) ok.push('NOx')
        if (c3 > 0) ok.push('escorrentía')
        if (c4 > 0) ok.push('lumínica')
        if (c5 > 0) ok.push('ruido')
        return V(`${sum}/13 pts CNT total`, sum >= 9 ? 'ok' : 'partial',
          ok.length ? `Impactos negativos controlados: ${ok.join(' + ')}.` : 'Impactos contaminación evaluados.')
      })(),
      // 414
      '414-1': mat3J ? V('MAT 3 BES 6001', 'ok', 'BES 6001 incluye dimensiones sociales (CSR del proveedor).')
        : V('—', 'pending', 'Carga MAT 3.'),
      // 416
      '416-1': (((evaluations['Salud y Bienestar-SyB 4']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 4']?.points_obtained)) > 0 || ((evaluations['Salud y Bienestar-SyB 7']?.puntos_auditoria) ?? (evaluations['Salud y Bienestar-SyB 7']?.points_obtained)) > 0)
        ? V('SyB 4 + SyB 7 evaluados', 'ok', 'Agua potable + riesgos naturales — evaluación impactos salud ocupantes.')
        : V('—', 'pending', 'Evalúa SyB 4 y SyB 7.'),
    }
  }, [sensors, ci, ene1J, mat1J, mat3J, rsd1J, rsd2J, tra1J, gst1J, gst4J, ene1Co2, ene1Energy, cont2, matCat, sybCat, aguaCat, m2, evaluations, iros])

  // ── Resumen por serie ──
  const summary = useMemo(() => {
    const by = { '200': { ok: 0, partial: 0, pending: 0, total: 0 }, '300': { ok: 0, partial: 0, pending: 0, total: 0 }, '400': { ok: 0, partial: 0, pending: 0, total: 0 } }
    GRI_STANDARDS.forEach(s => {
      s.disclosures.forEach(d => {
        by[s.series].total += 1
        by[s.series][disclosureValues[d.code]?.status || 'pending'] += 1
      })
    })
    const all = Object.values(by).reduce((a, b) => ({ ok: a.ok + b.ok, partial: a.partial + b.partial, pending: a.pending + b.pending, total: a.total + b.total }), { ok: 0, partial: 0, pending: 0, total: 0 })
    return { by, all }
  }, [disclosureValues])

  const visibleStandards = seriesFilter === 'all' ? GRI_STANDARDS : GRI_STANDARDS.filter(s => s.series === seriesFilter)

  const handleCSV = () => {
    const rows = []
    GRI_STANDARDS.forEach(s => {
      s.disclosures.forEach(d => {
        const v = disclosureValues[d.code]
        rows.push({
          serie: s.series, estandar: `GRI ${s.id}`, estandar_nombre: s.name,
          disclosure: d.code, disclosure_nombre: d.name,
          valor: v.value, estado: v.status, fuente: d.src,
        })
      })
    })
    downloadCSV(rows, `InformeGRI_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const handlePDF = () => {
    const sections = [
      {
        title: 'Resumen — Cobertura GRI',
        cols: ['Serie', 'Reportable', 'Parcial', 'Pendiente', 'Total'],
        rows: [
          ['200 Económico', summary.by['200'].ok, summary.by['200'].partial, summary.by['200'].pending, summary.by['200'].total],
          ['300 Ambiental', summary.by['300'].ok, summary.by['300'].partial, summary.by['300'].pending, summary.by['300'].total],
          ['400 Social',    summary.by['400'].ok, summary.by['400'].partial, summary.by['400'].pending, summary.by['400'].total],
          ['Total',         summary.all.ok, summary.all.partial, summary.all.pending, summary.all.total],
        ],
      },
      ...GRI_STANDARDS.map(s => ({
        title: `GRI ${s.id} — ${s.name}`,
        cols: ['Disclosure', 'Indicador', 'Valor', 'Estado', 'Fuente'],
        rows: s.disclosures.map(d => {
          const v = disclosureValues[d.code]
          return [d.code, d.name, v.value, STATUS[v.status].label, d.src]
        }),
      })),
    ]
    openPrintWindow({ title: 'Informe GRI — Estándares derivados de BREEAM', projectName: project?.name || 'Proyecto', sections })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>
            Global Reporting Initiative · GRI Standards 2021
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>Informe GRI — 13 estándares</div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>Serie 200 (económico) · 300 (ambiental) · 400 (social) — todos derivados de tu BREEAM</div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: C.olive, lineHeight: 1 }}>{summary.all.ok}</div>
            <div style={{ fontSize: '0.62rem', color: '#B0A89E', marginTop: '0.2rem' }}>Reportables</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: C.amber, lineHeight: 1 }}>{summary.all.partial}</div>
            <div style={{ fontSize: '0.62rem', color: '#B0A89E', marginTop: '0.2rem' }}>Parciales</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: '#B0A89E', lineHeight: 1 }}>{summary.all.pending}</div>
            <div style={{ fontSize: '0.62rem', color: '#B0A89E', marginTop: '0.2rem' }}>Pendientes</div>
          </div>
          <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(245,240,234,0.2)', paddingLeft: '1rem' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: '#F5F0EA', lineHeight: 1 }}>{summary.all.total}</div>
            <div style={{ fontSize: '0.62rem', color: '#B0A89E', marginTop: '0.2rem' }}>Disclosures</div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button onClick={loadLive} disabled={loading} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <ArrowsClockwise size={13} /> {loading ? 'Cargando…' : 'Refrescar'}
            </button>
            <button onClick={handleCSV} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <FileCsv size={13} /> CSV
            </button>
            <button onClick={handlePDF} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <FilePdf size={13} /> PDF
            </button>
          </div>
        </div>
      </div>

      {error && <div style={{ color: C.coral, fontSize: '0.8rem' }}>Error: {error}</div>}

      {/* Filtro por serie */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: `Todas (${summary.all.total})` },
          { id: '200', label: `Serie 200 — Económico (${summary.by['200'].total})` },
          { id: '300', label: `Serie 300 — Ambiental (${summary.by['300'].total})` },
          { id: '400', label: `Serie 400 — Social (${summary.by['400'].total})` },
        ].map(b => (
          <button key={b.id} onClick={() => setSeriesFilter(b.id)} style={{
            background: seriesFilter === b.id ? C.text : C.bg, color: seriesFilter === b.id ? '#F5F0EA' : C.muted,
            border: `1px solid ${seriesFilter === b.id ? C.text : C.border}`, borderRadius: '5px', padding: '5px 11px', fontSize: '0.74rem', cursor: 'pointer', fontWeight: 500,
          }}>{b.label}</button>
        ))}
      </div>

      {/* IROs operativos detectados — integración con módulo Monitorización */}
      {iros.length > 0 && (() => {
        const impact = iros.filter(i => i.type === 'impact').length
        const risk = iros.filter(i => i.type === 'risk').length
        const opp = iros.filter(i => i.type === 'opportunity').length
        const totalEur = iros.reduce((s, i) => s + (Number(i.financial_estimate_eur) || 0), 0)
        return (
          <div style={{ ...CARD, borderLeft: `4px solid ${C.olive}` }}>
            <div style={{ marginBottom: '0.7rem' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text }}>IROs operativos identificados</div>
              <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 2 }}>Alimentan GRI 201-2 (implicaciones financieras cambio climático) y enriquecen GRI 305 (emisiones GEI).</div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              <span style={{ background: '#FDF5F3', border: `1px solid ${C.coral}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: C.coral, fontWeight: 600 }}>{impact} Impactos</span>
              <span style={{ background: '#FFF6E5', border: `1px solid ${C.amber}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: C.amber, fontWeight: 600 }}>{risk} Riesgos</span>
              <span style={{ background: '#F0F5EC', border: `1px solid ${C.olive}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: C.olive, fontWeight: 600 }}>{opp} Oportunidades</span>
              {totalEur > 0 && <span style={{ background: '#F5F0EA', border: `1px solid ${C.border}`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: C.text, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(totalEur).toLocaleString('es-ES')} €/año</span>}
            </div>
          </div>
        )
      })()}

      {/* Cards por estándar */}
      {visibleStandards.map(s => {
        return (
          <div key={s.id} style={{ ...CARD, borderLeft: `4px solid ${s.accent}` }}>
            <div style={{ marginBottom: '0.875rem', paddingBottom: '0.5rem', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '0.65rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>GRI {s.id} · Serie {s.series}</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: C.text }}>{s.name}</div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '70px' }} />
                <col />
                <col style={{ width: '260px' }} />
                <col style={{ width: '110px' }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left' }}>Disclosure</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left' }}>Valor</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {s.disclosures.map((d, i) => {
                  const v = disclosureValues[d.code]
                  const st = STATUS[v.status]
                  const SIc = st.Icon
                  return (
                    <tr key={d.code} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: `1px solid #F0EBE2` }}>
                      <td style={{ padding: '0.5rem 0.6rem', fontFamily: "'IBM Plex Mono', monospace", color: C.muted, verticalAlign: 'top' }}>{d.code}</td>
                      <td style={{ padding: '0.5rem 0.6rem', color: C.text, verticalAlign: 'top' }}>
                        <div style={{ fontWeight: 600 }}>{d.name}</div>
                        <div style={{ fontSize: '0.7rem', color: C.muted, fontStyle: 'italic', marginTop: '0.15rem' }}>{v.detail}</div>
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', fontFamily: "'IBM Plex Mono', monospace", color: st.color, fontWeight: 600, verticalAlign: 'top' }}>{v.value}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center', verticalAlign: 'top' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: st.color, fontWeight: 600, fontSize: '0.7rem' }}>
                          <SIc size={11} weight={v.status === 'ok' ? 'fill' : 'regular'} /> {st.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })}

      <div style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start' }}>
          <ChartBar size={16} style={{ color: C.muted, flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '0.75rem', color: C.muted, margin: 0, lineHeight: '1.6' }}>
            <strong style={{ color: '#5C4A3A' }}>Metodología:</strong> los valores se calculan automáticamente desde calculadoras BREEAM (ENE 1, MAT 1, MAT 3, RSD 1, RSD 2, TRA 1, GST 1, GST 4), Building Resource Passport DGNB (Econ Circular CI), y lecturas mensuales en <code style={{ fontFamily: 'inherit', background: C.bg, padding: '1px 4px', borderRadius: '3px', border: `1px solid ${C.border}` }}>building_sensors</code>. Los estándares de la serie 400 con disclosures cualitativos (engagement, formación) requieren documentación adicional que el informe BREEAM puede acreditar. Para reporte GRI formal, considerar la auditoría externa por GRI Certified Training Partner. Factores de conversión utilizados: red eléctrica España 2024 = {GRID_FACTOR_ES} kgCO₂e/kWh (REE), gas natural = {GAS_FACTOR} kgCO₂e/kWh (MITECO).
          </p>
        </div>
      </div>
    </div>
  )
}
